import * as React from "react";
import { useFinance } from "@/context/FinanceContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GripVertical, Edit, Trash2, Plus, Package } from "lucide-react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { toast } from "@/components/ui/use-toast";
import { ImportCadastroButton } from "@/components/ImportCadastroButtons";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import { PermissionAlert } from "@/components/PermissionAlert";

// Category Manager Expanded
export const CategoryManagerExpanded: React.FC = () => {
  const {
    categories,
    addCategory,
    updateCategory,
    removeCategory
  } = useFinance();
  const { checkPermission, canView, showAlert, setShowAlert, alertAction, loading } = usePermissionCheck('chart_of_accounts');
  const [newName, setNewName] = React.useState("");
  const [newColor, setNewColor] = React.useState<string>("#64748B");
  const [editing, setEditing] = React.useState<string | null>(null);
  const [editName, setEditName] = React.useState("");
  const [editColor, setEditColor] = React.useState<string>("#64748B");
  const [creating, setCreating] = React.useState(false);
  const [newType, setNewType] = React.useState<"receita" | "despesa">("despesa");
  const [editType, setEditType] = React.useState<"receita" | "despesa">("despesa");
  const visibleCategories = React.useMemo(() => categories.filter(c => !c.hidden), [categories]);

  // DnD logic
  const ORDER_KEY = "cat-order-v1";
  const sensors = useSensors(useSensor(PointerSensor));
  const [order, setOrder] = React.useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(ORDER_KEY);
      if (raw) {
        const arr = JSON.parse(raw) as string[];
        if (Array.isArray(arr)) return arr;
      }
    } catch {}
    return visibleCategories.map(c => c.id);
  });
  React.useEffect(() => {
    try {
      localStorage.setItem(ORDER_KEY, JSON.stringify(order));
    } catch {}
  }, [order]);
  React.useEffect(() => {
    const ids = visibleCategories.map(c => c.id);
    setOrder(prev => {
      const setPrev = new Set(prev);
      const merged = [...prev.filter(id => ids.includes(id)), ...ids.filter(id => !setPrev.has(id))];
      return merged;
    });
  }, [categories]);
  const orderedCategories = React.useMemo(() => {
    const map = new Map(visibleCategories.map(c => [c.id, c] as const));
    const list = order.map(id => map.get(id)).filter(Boolean) as typeof visibleCategories;
    const remaining = visibleCategories.filter(c => !order.includes(c.id));
    return [...list, ...remaining];
  }, [visibleCategories, order]);
  const handleDragEnd = (event: DragEndEvent) => {
    const {
      active,
      over
    } = event;
    if (!over || active.id === over.id) return;
    const current = orderedCategories.map(c => c.id);
    const from = current.indexOf(String(active.id));
    const to = current.indexOf(String(over.id));
    if (from !== -1 && to !== -1) setOrder(arrayMove(current, from, to));
  };

  // Check view permission
  if (!loading && !canView()) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Plano de Contas</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
            <p>Você não tem permissão para visualizar este módulo.</p>
            <p className="text-sm mt-2">Solicite ao proprietário da organização que altere suas permissões.</p>
          </div>
        </CardContent>
      </Card>
    );
  }
  function CategoryRow({
    item
  }: {
    item: {
      id: string;
      name: string;
      color?: string;
      type?: "receita" | "despesa";
    };
  }) {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging
    } = useSortable({
      id: item.id
    });
    const style: React.CSSProperties = {
      transform: CSS.Translate.toString(transform),
      transition,
      opacity: isDragging ? 0.9 : 1
    };
    return <div ref={setNodeRef} style={style} className="grid grid-cols-1 md:grid-cols-12 items-center gap-2 md:gap-4 px-3 py-3">
        <div className="md:col-span-9 flex items-center gap-2">
          <button className="cursor-grab active:cursor-grabbing p-1 rounded-sm text-muted-foreground" aria-label="Arrastar para reordenar" {...attributes} {...listeners}>
            <GripVertical className="h-4 w-4 opacity-70" />
          </button>
          <span className="truncate font-medium">{item.name} <span className="text-muted-foreground font-normal">(Tipo: {item.type === "receita" ? "Receita" : "Despesa"})</span></span>
        </div>
        <div className="md:col-span-3 flex items-center justify-end gap-3">
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => {
            if (!checkPermission('edit')) return;
            setEditing(item.id);
            setEditName(item.name);
            setEditColor(item.color || "#64748B");
            setEditType(item.type as any || "despesa");
          }}><Edit className="h-4 w-4" /></Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="destructive"><Trash2 className="h-4 w-4" /></Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Excluir plano de contas</AlertDialogTitle>
                  <AlertDialogDescription>
                    Tem certeza que deseja excluir "{item.name}"? Esta ação não pode ser desfeita.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={() => {
                  if (!checkPermission('delete')) return;
                  removeCategory(item.id);
                  toast({
                    title: "Plano de contas excluído",
                    description: `"${item.name}" foi removido.`,
                    duration: 500
                  });
                }}>Confirmar</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>;
  }
  return <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Plano de Contas</span>
          <span className="text-sm font-normal text-muted-foreground">
            {visibleCategories.length} {visibleCategories.length === 1 ? 'categoria' : 'categorias'}
          </span>
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between mb-2">
          <ImportCadastroButton type="categories" label="Importar Plano de Contas" />
          </div>
          <div className="h-11 bg-primary text-primary-foreground px-3 flex items-center justify-between">
            <h3 className="font-semibold">Plano de Contas</h3>
            <Button size="sm" variant="secondary" className="h-9 bg-white text-primary hover:bg-white/90" onClick={() => { if (!checkPermission('create')) return; setCreating(true); }}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={orderedCategories.map(c => c.id)} strategy={verticalListSortingStrategy}>
              <div className="divide-y">
                {orderedCategories.map(c => <CategoryRow key={c.id} item={c} />)}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      </CardContent>

      {/* Modal de edição de categoria */}
      <Dialog open={!!editing} onOpenChange={open => {
      if (!open) setEditing(null);
    }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Plano de Contas</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="edit-category-name" required>Nome</Label>
              <Input id="edit-category-name" value={editName} onChange={e => setEditName(e.target.value)} className="h-10" required />
            </div>

            <div className="space-y-2">
              <Label required>Tipo</Label>
              <Select onValueChange={v => setEditType(v as any)} defaultValue={editType}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent className="z-50 bg-background text-foreground">
                  <SelectItem value="receita">Receita</SelectItem>
                  <SelectItem value="despesa">Despesa</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancelar</Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button>Salvar</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Confirmar alterações</AlertDialogTitle>
                  <AlertDialogDescription>Deseja salvar as alterações deste plano de contas?</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Voltar</AlertDialogCancel>
                  <AlertDialogAction onClick={() => {
                  if (!editing) return;
                  
                  // Validação de duplicação (exclui o próprio item)
                  const normalizedEditName = (editName || "").trim().toLowerCase();
                  const isDuplicate = visibleCategories.some(
                    cat => 
                      cat.id !== editing && 
                      cat.name.toLowerCase() === normalizedEditName
                  );
                  
                  if (isDuplicate) {
                    toast({
                      title: "Nome duplicado",
                      description: `Já existe um plano de contas com o nome "${editName?.trim()}".`,
                      variant: "destructive",
                      duration: 3000
                    });
                    return;
                  }
                  
                  updateCategory(editing, editName || undefined, editColor || undefined, editType);
                  toast({
                    title: "Plano de contas atualizado",
                    duration: 500
                  });
                  setEditing(null);
                }}>Confirmar</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de novo cadastro de categoria */}
      <Dialog open={creating} onOpenChange={open => setCreating(open)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo Plano de Contas</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="new-category-name" required>Nome</Label>
              <Input id="new-category-name" value={newName} onChange={e => setNewName(e.target.value)} className="h-10" required />
            </div>

            <div className="space-y-2">
              <Label required>Tipo</Label>
              <Select onValueChange={v => setNewType(v as any)} defaultValue={newType}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent className="z-50 bg-background text-foreground">
                  <SelectItem value="receita">Receita</SelectItem>
                  <SelectItem value="despesa">Despesa</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreating(false)}>Cancelar</Button>
            <Button onClick={() => {
            if (!newName.trim()) return;
            
            // Validação de duplicação
            const normalizedNewName = newName.trim().toLowerCase();
            const isDuplicate = visibleCategories.some(
              cat => cat.name.toLowerCase() === normalizedNewName
            );
            
            if (isDuplicate) {
              toast({
                title: "Nome duplicado",
                description: `Já existe um plano de contas com o nome "${newName.trim()}".`,
                variant: "destructive",
                duration: 3000
              });
              return;
            }
            
            addCategory(newName.trim(), newColor || "#64748B", newType);
            setNewName("");
            setNewColor("#64748B");
            setNewType("despesa");
            setCreating(false);
          }}>Adicionar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alert de permissão */}
      <PermissionAlert 
        open={showAlert} 
        onOpenChange={setShowAlert} 
        action={alertAction} 
      />
    </Card>;
};

// Cost Center Manager Expanded
export const CostCenterManagerExpanded: React.FC = () => {
  const {
    costCenters,
    addCostCenter,
    updateCostCenter,
    removeCostCenter
  } = useFinance();
  const { checkPermission, canView, showAlert, setShowAlert, alertAction, loading } = usePermissionCheck('cost_centers');
  const [newName, setNewName] = React.useState("");
  const [editing, setEditing] = React.useState<string | null>(null);
  const [editName, setEditName] = React.useState("");
  const [creating, setCreating] = React.useState(false);

  // DnD logic
  const ORDER_KEY = "cc-order-v1";
  const sensors = useSensors(useSensor(PointerSensor));
  const [order, setOrder] = React.useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(ORDER_KEY);
      if (raw) {
        const arr = JSON.parse(raw) as string[];
        if (Array.isArray(arr)) return arr;
      }
    } catch {}
    return costCenters.map(c => c.id);
  });
  React.useEffect(() => {
    try {
      localStorage.setItem(ORDER_KEY, JSON.stringify(order));
    } catch {}
  }, [order]);
  React.useEffect(() => {
    const ids = costCenters.map(c => c.id);
    setOrder(prev => {
      const setPrev = new Set(prev);
      const merged = [...prev.filter(id => ids.includes(id)), ...ids.filter(id => !setPrev.has(id))];
      return merged;
    });
  }, [costCenters]);
  const orderedCostCenters = React.useMemo(() => {
    const map = new Map(costCenters.map(c => [c.id, c] as const));
    const list = order.map(id => map.get(id)).filter(Boolean) as typeof costCenters;
    const remaining = costCenters.filter(c => !order.includes(c.id));
    return [...list, ...remaining];
  }, [costCenters, order]);
  const handleDragEnd = (event: DragEndEvent) => {
    const {
      active,
      over
    } = event;
    if (!over || active.id === over.id) return;
    const current = orderedCostCenters.map(c => c.id);
    const from = current.indexOf(String(active.id));
    const to = current.indexOf(String(over.id));
    if (from !== -1 && to !== -1) setOrder(arrayMove(current, from, to));
  };

  // Check view permission
  if (!loading && !canView()) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Centro de Custos</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
            <p>Você não tem permissão para visualizar este módulo.</p>
            <p className="text-sm mt-2">Solicite ao proprietário da organização que altere suas permissões.</p>
          </div>
        </CardContent>
      </Card>
    );
  }
  function CostCenterRow({
    item
  }: {
    item: {
      id: string;
      name: string;
    };
  }) {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging
    } = useSortable({
      id: item.id
    });
    const style: React.CSSProperties = {
      transform: CSS.Translate.toString(transform),
      transition,
      opacity: isDragging ? 0.9 : 1
    };
    return <div ref={setNodeRef} style={style} className="grid grid-cols-1 md:grid-cols-12 items-center gap-2 md:gap-4 px-3 py-3">
        <div className="md:col-span-6 flex items-center gap-2">
          <button className="cursor-grab active:cursor-grabbing p-1 rounded-sm text-muted-foreground" aria-label="Arrastar para reordenar" {...attributes} {...listeners}>
            <GripVertical className="h-4 w-4 opacity-70" />
          </button>
          <span className="truncate font-medium">{item.name}</span>
        </div>
        <div className="md:col-span-6 flex items-center justify-end gap-2">
          <Button size="sm" variant="outline" onClick={() => {
          if (!checkPermission('edit')) return;
          setEditing(item.id);
          setEditName(item.name);
        }}>
            <Edit className="h-4 w-4" />
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" variant="destructive"><Trash2 className="h-4 w-4" /></Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Excluir centro de custo</AlertDialogTitle>
                <AlertDialogDescription>
                  Tem certeza que deseja excluir "{item.name}"? Esta ação não pode ser desfeita.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={() => {
                if (!checkPermission('delete')) return;
                removeCostCenter(item.id);
                toast({
                  title: "Centro de custo excluído",
                  description: `"${item.name}" foi removido.`,
                  duration: 500
                });
              }}>
                  Confirmar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>;
  }
  return <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Centro de Custos</span>
          <span className="text-sm font-normal text-muted-foreground">
            {costCenters.length} {costCenters.length === 1 ? 'centro' : 'centros'}
          </span>
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <ImportCadastroButton type="costCenters" label="Importar Centros de Custo" />
          <div className="ml-auto">
            <Button size="sm" onClick={() => { if (!checkPermission('create')) return; setCreating(true); }}>
              <Plus className="h-4 w-4 md:mr-1" />
              <span className="hidden md:inline">Adicionar</span>
            </Button>
          </div>
        </div>

        <div className="border rounded-md">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={orderedCostCenters.map(c => c.id)} strategy={verticalListSortingStrategy}>
              <div className="divide-y">
                {orderedCostCenters.map(c => <CostCenterRow key={c.id} item={c} />)}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      </CardContent>

      {/* Modal de edição */}
      <Dialog open={!!editing} onOpenChange={open => {
      if (!open) setEditing(null);
    }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Centro de Custo</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="edit-cc-name">Nome</Label>
              <Input id="edit-cc-name" value={editName} onChange={e => setEditName(e.target.value)} className="h-10" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancelar</Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button>Salvar</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Confirmar alterações</AlertDialogTitle>
                  <AlertDialogDescription>Deseja salvar as alterações deste centro de custo?</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Voltar</AlertDialogCancel>
                  <AlertDialogAction onClick={() => {
                  if (!editing) return;
                  
                  // Validação de duplicação (exclui o próprio item)
                  const normalizedEditName = (editName || "").trim().toLowerCase();
                  const isDuplicate = costCenters.some(
                    cc => 
                      cc.id !== editing && 
                      cc.name.toLowerCase() === normalizedEditName
                  );
                  
                  if (isDuplicate) {
                    toast({
                      title: "Nome duplicado",
                      description: `Já existe um centro de custo com o nome "${editName?.trim()}".`,
                      variant: "destructive",
                      duration: 3000
                    });
                    return;
                  }
                  
                  updateCostCenter(editing, editName || costCenters.find(c => c.id === editing)?.name || "");
                  toast({
                    title: "Centro de custo atualizado",
                    duration: 500
                  });
                  setEditing(null);
                }}>Confirmar</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de novo centro */}
      <Dialog open={creating} onOpenChange={open => setCreating(open)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo Centro de Custo</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="new-cc-name">Nome</Label>
              <Input id="new-cc-name" value={newName} onChange={e => setNewName(e.target.value)} className="h-10" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreating(false)}>Cancelar</Button>
            <Button onClick={() => {
            if (!newName.trim()) return;
            
            // Validação de duplicação
            const normalizedName = newName.trim().toLowerCase();
            const isDuplicate = costCenters.some(
              cc => cc.name.toLowerCase() === normalizedName
            );
            
            if (isDuplicate) {
              toast({
                title: "Nome duplicado",
                description: `Já existe um centro de custo com o nome "${newName.trim()}".`,
                variant: "destructive",
                duration: 3000
              });
              return;
            }
            
            addCostCenter(newName.trim());
            setNewName("");
            setCreating(false);
          }}>Adicionar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alert de permissão */}
      <PermissionAlert 
        open={showAlert} 
        onOpenChange={setShowAlert} 
        action={alertAction} 
      />
    </Card>;
};

// Account Manager Expanded
export const AccountManagerExpanded: React.FC = () => {
  const {
    accounts,
    addAccount,
    updateAccount,
    removeAccount
  } = useFinance();
  const { checkPermission, canView, showAlert, setShowAlert, alertAction, loading } = usePermissionCheck('accounts');
  const [newName, setNewName] = React.useState("");
  const [newColor, setNewColor] = React.useState<string>("#64748B");
  const [newInitialStr, setNewInitialStr] = React.useState<string>("0");
  const [newAgencia, setNewAgencia] = React.useState("");
  const [newContaCorrente, setNewContaCorrente] = React.useState("");
  const [editing, setEditing] = React.useState<string | null>(null);
  const [editName, setEditName] = React.useState("");
  const [editColor, setEditColor] = React.useState<string>("#64748B");
  const [editInitialStr, setEditInitialStr] = React.useState<string>("0");
  const [editAgencia, setEditAgencia] = React.useState("");
  const [editContaCorrente, setEditContaCorrente] = React.useState("");
  const [creating, setCreating] = React.useState(false);
  const NUMBER_REGEX = /^-?\d*(?:[.,]\d{0,2})?$/;
  const parseToNumber = (s: string) => {
    const n = parseFloat((s ?? "").replace(",", "."));
    return isNaN(n) ? 0 : n;
  };

  // DnD logic
  const ORDER_KEY = "acc-order-v1";
  const sensors = useSensors(useSensor(PointerSensor));
  const [order, setOrder] = React.useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(ORDER_KEY);
      if (raw) {
        const arr = JSON.parse(raw) as string[];
        if (Array.isArray(arr)) return arr;
      }
    } catch {}
    return accounts.map(a => a.id);
  });
  React.useEffect(() => {
    try {
      localStorage.setItem(ORDER_KEY, JSON.stringify(order));
    } catch {}
  }, [order]);
  React.useEffect(() => {
    const ids = accounts.map(a => a.id);
    setOrder(prev => {
      const setPrev = new Set(prev);
      const merged = [...prev.filter(id => ids.includes(id)), ...ids.filter(id => !setPrev.has(id))];
      return merged;
    });
  }, [accounts]);
  const orderedAccounts = React.useMemo(() => {
    const map = new Map(accounts.map(a => [a.id, a] as const));
    const list = order.map(id => map.get(id)).filter(Boolean) as typeof accounts;
    const remaining = accounts.filter(a => !order.includes(a.id));
    return [...list, ...remaining];
  }, [accounts, order]);
  const handleDragEnd = (event: DragEndEvent) => {
    const {
      active,
      over
    } = event;
    if (!over || active.id === over.id) return;
    const current = orderedAccounts.map(a => a.id);
    const from = current.indexOf(String(active.id));
    const to = current.indexOf(String(over.id));
    if (from !== -1 && to !== -1) setOrder(arrayMove(current, from, to));
  };

  // Check view permission
  if (!loading && !canView()) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Contas Financeiras</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
            <p>Você não tem permissão para visualizar este módulo.</p>
            <p className="text-sm mt-2">Solicite ao proprietário da organização que altere suas permissões.</p>
          </div>
        </CardContent>
      </Card>
    );
  }
  function AccountRow({
    item
  }: {
    item: {
      id: string;
      name: string;
      color?: string;
      initialBalance?: number;
      agencia?: string;
      contaCorrente?: string;
    };
  }) {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging
    } = useSortable({
      id: item.id
    });
    const style: React.CSSProperties = {
      transform: CSS.Translate.toString(transform),
      transition,
      opacity: isDragging ? 0.9 : 1
    };
    return <div ref={setNodeRef} style={style} className="grid grid-cols-1 md:grid-cols-12 items-center gap-2 md:gap-4 px-3 py-3">
        <div className="md:col-span-6 flex items-center gap-2">
          <button className="cursor-grab active:cursor-grabbing p-1 rounded-sm text-muted-foreground" aria-label="Arrastar para reordenar" {...attributes} {...listeners}>
            <GripVertical className="h-4 w-4 opacity-70" />
          </button>
          <div className="min-w-0">
            <span className="truncate font-medium block">{item.name}</span>
            {(item.agencia || item.contaCorrente) && (
              <span className="text-xs text-muted-foreground truncate block">
                {item.agencia ? `Ag: ${item.agencia}` : ""}{item.agencia && item.contaCorrente ? " · " : ""}{item.contaCorrente ? `CC: ${item.contaCorrente}` : ""}
              </span>
            )}
          </div>
        </div>
        <div className="md:col-span-6 flex items-center justify-end gap-3">
          <div className="flex items-center gap-1">
            <span className="text-muted-foreground text-xs md:text-sm">Saldo inicial:</span>
            <span className="tabular-nums text-sm">{new Intl.NumberFormat("pt-BR", {
              style: "currency",
              currency: "BRL"
            }).format(item.initialBalance ?? 0)}</span>
          </div>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => {
            if (!checkPermission('edit')) return;
            setEditing(item.id);
            setEditName(item.name);
            setEditColor(item.color ?? '#64748B');
            setEditInitialStr(String(item.initialBalance ?? 0));
            setEditAgencia(item.agencia ?? '');
            setEditContaCorrente(item.contaCorrente ?? '');
          }}><Edit className="h-4 w-4" /></Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="destructive"><Trash2 className="h-4 w-4" /></Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Excluir conta</AlertDialogTitle>
                  <AlertDialogDescription>
                    Tem certeza que deseja excluir "{item.name}"? Esta ação não pode ser desfeita.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={() => {
                  if (!checkPermission('delete')) return;
                  removeAccount(item.id);
                  toast({
                    title: "Conta excluída",
                    description: `"${item.name}" foi removida.`,
                    duration: 500
                  });
                }}>Confirmar</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>;
  }
  return <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Contas Financeiras</span>
          <span className="text-sm font-normal text-muted-foreground">
            {accounts.length} {accounts.length === 1 ? 'conta' : 'contas'}
          </span>
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <ImportCadastroButton type="accounts" label="Importar Contas Financeiras" />
          <div className="ml-auto">
            <Button size="sm" onClick={() => { if (!checkPermission('create')) return; setCreating(true); }}>
              <Plus className="h-4 w-4 md:mr-1" />
              <span className="hidden md:inline">Adicionar</span>
            </Button>
          </div>
        </div>

        <div className="border rounded-md">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={orderedAccounts.map(a => a.id)} strategy={verticalListSortingStrategy}>
              <div className="divide-y">
                {orderedAccounts.map(a => <AccountRow key={a.id} item={a} />)}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      </CardContent>

      {/* Modal de edição de conta */}
      <Dialog open={!!editing} onOpenChange={open => {
      if (!open) setEditing(null);
    }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Conta</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="edit-account-name">Nome</Label>
              <Input id="edit-account-name" value={editName} onChange={e => setEditName(e.target.value)} className="h-10" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-account-initial">Saldo inicial</Label>
              <Input id="edit-account-initial" type="text" inputMode="decimal" value={editInitialStr} onChange={e => {
              const v = e.target.value;
              if (NUMBER_REGEX.test(v)) setEditInitialStr(v);
            }} className="h-10" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-account-agencia">Agência (opcional)</Label>
                <Input id="edit-account-agencia" value={editAgencia} onChange={e => setEditAgencia(e.target.value)} className="h-10" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-account-cc">Conta corrente (opcional)</Label>
                <Input id="edit-account-cc" value={editContaCorrente} onChange={e => setEditContaCorrente(e.target.value)} className="h-10" />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancelar</Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button>Salvar</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Confirmar alterações</AlertDialogTitle>
                  <AlertDialogDescription>Deseja salvar as alterações desta conta?</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Voltar</AlertDialogCancel>
                  <AlertDialogAction onClick={() => {
                  if (!editing) return;
                  
                  // Validação de duplicação (exclui o próprio item)
                  const normalizedEditName = (editName || "").trim().toLowerCase();
                  const isDuplicate = accounts.some(
                    acc => 
                      acc.id !== editing && 
                      acc.name.toLowerCase() === normalizedEditName
                  );
                  
                  if (isDuplicate) {
                    toast({
                      title: "Nome duplicado",
                      description: `Já existe uma conta com o nome "${editName?.trim()}".`,
                      variant: "destructive",
                      duration: 3000
                    });
                    return;
                  }
                  
                  const value = parseToNumber(editInitialStr);
                  updateAccount(editing, editName || undefined, editColor || undefined, value, { agencia: editAgencia.trim(), contaCorrente: editContaCorrente.trim() });
                  toast({
                    title: "Conta atualizada",
                    duration: 500
                  });
                  setEditing(null);
                }}>Confirmar</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de nova conta */}
      <Dialog open={creating} onOpenChange={open => setCreating(open)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nova Conta</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="new-account-name">Nome</Label>
              <Input id="new-account-name" value={newName} onChange={e => setNewName(e.target.value)} className="h-10" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-account-initial">Saldo inicial</Label>
              <Input id="new-account-initial" type="text" inputMode="decimal" value={newInitialStr} onChange={e => {
              const v = e.target.value;
              if (NUMBER_REGEX.test(v)) setNewInitialStr(v);
            }} className="h-10" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="new-account-agencia">Agência (opcional)</Label>
                <Input id="new-account-agencia" value={newAgencia} onChange={e => setNewAgencia(e.target.value)} className="h-10" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-account-cc">Conta corrente (opcional)</Label>
                <Input id="new-account-cc" value={newContaCorrente} onChange={e => setNewContaCorrente(e.target.value)} className="h-10" />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreating(false)}>Cancelar</Button>
            <Button onClick={() => {
            if (!newName.trim()) return;
            
            // Validação de duplicação
            const normalizedName = newName.trim().toLowerCase();
            const isDuplicate = accounts.some(
              acc => acc.name.toLowerCase() === normalizedName
            );
            
            if (isDuplicate) {
              toast({
                title: "Nome duplicado",
                description: `Já existe uma conta com o nome "${newName.trim()}".`,
                variant: "destructive",
                duration: 3000
              });
              return;
            }
            
            const value = parseToNumber(newInitialStr);
            addAccount(newName.trim(), newColor, value, { agencia: newAgencia.trim(), contaCorrente: newContaCorrente.trim() });
            setNewName("");
            setNewColor("#64748B");
            setNewInitialStr("0");
            setNewAgencia("");
            setNewContaCorrente("");
            setCreating(false);
          }}>Adicionar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alert de permissão */}
      <PermissionAlert 
        open={showAlert} 
        onOpenChange={setShowAlert} 
        action={alertAction} 
      />
    </Card>;
};

// Company Manager Expanded
export const CompanyManagerExpanded: React.FC = () => {
  // Single-company mode: companies disabled
  const companies: Array<{
    id: string;
    name: string;
    acronym: string;
  }> = [];
  const removeCompany = (_id: string) => {};
  const updateCompany = (_id: string, _name: string, _acronym: string) => {};
  const addCompany = (_name: string, _acronym: string) => {};
  const [newName, setNewName] = React.useState("");
  const [newAcronym, setNewAcronym] = React.useState("");
  const [editing, setEditing] = React.useState<string | null>(null);
  const [editName, setEditName] = React.useState("");
  const [editAcronym, setEditAcronym] = React.useState("");
  const [creating, setCreating] = React.useState(false);

  // DnD logic
  const ORDER_KEY = "comp-order-v1";
  const sensors = useSensors(useSensor(PointerSensor));
  const [order, setOrder] = React.useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(ORDER_KEY);
      if (raw) {
        const arr = JSON.parse(raw) as string[];
        if (Array.isArray(arr)) return arr;
      }
    } catch {}
    return [];
  });
  React.useEffect(() => {
    try {
      localStorage.setItem(ORDER_KEY, JSON.stringify(order));
    } catch {}
  }, [order]);
  React.useEffect(() => {}, []);
  const orderedCompanies = React.useMemo(() => {
    const map = new Map(companies.map(c => [c.id, c] as const));
    const list = order.map(id => map.get(id)).filter(Boolean) as typeof companies;
    const remaining = companies.filter(c => !order.includes(c.id));
    return [...list, ...remaining];
  }, [companies, order]);
  const handleDragEnd = (event: DragEndEvent) => {
    const {
      active,
      over
    } = event;
    if (!over || active.id === over.id) return;
    const current = orderedCompanies.map(c => c.id);
    const from = current.indexOf(String(active.id));
    const to = current.indexOf(String(over.id));
    if (from !== -1 && to !== -1) setOrder(arrayMove(current, from, to));
  };
  function CompanyRow({
    item
  }: {
    item: {
      id: string;
      name: string;
      acronym: string;
    };
  }) {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging
    } = useSortable({
      id: item.id
    });
    const style: React.CSSProperties = {
      transform: CSS.Translate.toString(transform),
      transition,
      opacity: isDragging ? 0.9 : 1
    };
    return <div ref={setNodeRef} style={style} className="grid grid-cols-1 md:grid-cols-12 items-center gap-2 md:gap-4 px-3 py-3">
        <div className="md:col-span-8 flex items-center gap-2">
          <button className="cursor-grab active:cursor-grabbing p-1 rounded-sm text-muted-foreground" aria-label="Arrastar para reordenar" {...attributes} {...listeners}>
            <GripVertical className="h-4 w-4 opacity-70" />
          </button>
          <div className="flex flex-col">
            <span className="truncate font-medium">{item.name}</span>
            <span className="text-sm text-muted-foreground truncate">Sigla: {item.acronym}</span>
          </div>
        </div>
        <div className="md:col-span-4 flex items-center justify-end gap-2">
          <Button size="sm" variant="outline" onClick={() => {
          setEditing(item.id);
          setEditName(item.name);
          setEditAcronym(item.acronym);
        }}>
            <Edit className="h-4 w-4" />
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" variant="destructive"><Trash2 className="h-4 w-4" /></Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Excluir empresa</AlertDialogTitle>
                <AlertDialogDescription>
                  Tem certeza que deseja excluir "{item.name}"? Esta ação não pode ser desfeita.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={() => {
                removeCompany(item.id);
                toast({
                  title: "Empresa excluída",
                  description: `"${item.name}" foi removida.`,
                  duration: 500
                });
              }}>
                  Confirmar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>;
  }
  return <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Empresas</span>
          <span className="text-sm font-normal text-muted-foreground">
            {companies.length} {companies.length === 1 ? 'empresa' : 'empresas'}
          </span>
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="h-11 bg-primary text-primary-foreground px-3 flex items-center justify-between">
            <h3 className="font-semibold">Empresas</h3>
            <Button size="sm" variant="secondary" className="h-9 bg-white text-primary hover:bg-white/90" onClick={() => setCreating(true)}><Plus className="h-4 w-4" /></Button>
          </div>

          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={orderedCompanies.map(c => c.id)} strategy={verticalListSortingStrategy}>
              <div className="divide-y">
                {orderedCompanies.map(c => <CompanyRow key={c.id} item={c} />)}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      </CardContent>

      {/* Modal de edição */}
      <Dialog open={!!editing} onOpenChange={open => {
      if (!open) setEditing(null);
    }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Empresa</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="edit-company-name">Nome</Label>
              <Input id="edit-company-name" value={editName} onChange={e => setEditName(e.target.value)} className="h-10" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-company-acronym" required>Sigla</Label>
              <Input id="edit-company-acronym" value={editAcronym} onChange={e => setEditAcronym(e.target.value.toUpperCase())} className="h-10" placeholder="Ex: ACME" maxLength={10} required />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancelar</Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button>Salvar</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Confirmar alterações</AlertDialogTitle>
                  <AlertDialogDescription>Deseja salvar as alterações desta empresa?</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Voltar</AlertDialogCancel>
                  <AlertDialogAction onClick={() => {
                  if (!editing || !editAcronym.trim()) return;
                  updateCompany(editing, editName || companies.find(c => c.id === editing)?.name || "", editAcronym.trim());
                  toast({
                    title: "Empresa atualizada",
                    duration: 500
                  });
                  setEditing(null);
                }}>Confirmar</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de nova empresa */}
      <Dialog open={creating} onOpenChange={open => setCreating(open)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nova Empresa</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="new-company-name">Nome</Label>
              <Input id="new-company-name" value={newName} onChange={e => setNewName(e.target.value)} className="h-10" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-company-acronym" required>Sigla</Label>
              <Input id="new-company-acronym" value={newAcronym} onChange={e => setNewAcronym(e.target.value.toUpperCase())} className="h-10" placeholder="Ex: ACME" maxLength={10} required />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreating(false)}>Cancelar</Button>
            <Button onClick={() => {
            if (!newName.trim() || !newAcronym.trim()) return;
            addCompany(newName.trim(), newAcronym.trim());
            setNewName("");
            setNewAcronym("");
            setCreating(false);
          }} disabled={!newName.trim() || !newAcronym.trim()}>
              Adicionar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>;
};

// Payment Method Manager Expanded
export const PaymentMethodManagerExpanded: React.FC = () => {
  const {
    paymentMethods,
    addPaymentMethod,
    updatePaymentMethod,
    removePaymentMethod
  } = useFinance();
  const { checkPermission, canView, showAlert, setShowAlert, alertAction, loading } = usePermissionCheck('payment_methods');
  const [newName, setNewName] = React.useState("");
  const [editing, setEditing] = React.useState<string | null>(null);
  const [editName, setEditName] = React.useState("");
  const [creating, setCreating] = React.useState(false);

  // DnD logic
  const ORDER_KEY = "pm-order-v1";
  const sensors = useSensors(useSensor(PointerSensor));
  const [order, setOrder] = React.useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(ORDER_KEY);
      if (raw) {
        const arr = JSON.parse(raw) as string[];
        if (Array.isArray(arr)) return arr;
      }
    } catch {}
    return paymentMethods.map(p => p.id);
  });
  React.useEffect(() => {
    try {
      localStorage.setItem(ORDER_KEY, JSON.stringify(order));
    } catch {}
  }, [order]);
  React.useEffect(() => {
    const ids = paymentMethods.map(p => p.id);
    setOrder(prev => {
      const setPrev = new Set(prev);
      const merged = [...prev.filter(id => ids.includes(id)), ...ids.filter(id => !setPrev.has(id))];
      return merged;
    });
  }, [paymentMethods]);
  const orderedPaymentMethods = React.useMemo(() => {
    const map = new Map(paymentMethods.map(p => [p.id, p] as const));
    const list = order.map(id => map.get(id)).filter(Boolean) as typeof paymentMethods;
    const remaining = paymentMethods.filter(p => !order.includes(p.id));
    return [...list, ...remaining];
  }, [paymentMethods, order]);
  const handleDragEnd = (event: DragEndEvent) => {
    const {
      active,
      over
    } = event;
    if (!over || active.id === over.id) return;
    const current = orderedPaymentMethods.map(p => p.id);
    const from = current.indexOf(String(active.id));
    const to = current.indexOf(String(over.id));
    if (from !== -1 && to !== -1) setOrder(arrayMove(current, from, to));
  };

  // Check view permission
  if (!loading && !canView()) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Formas de Pagamento</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
            <p>Você não tem permissão para visualizar este módulo.</p>
            <p className="text-sm mt-2">Solicite ao proprietário da organização que altere suas permissões.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  function PaymentMethodRow({
    item
  }: {
    item: {
      id: string;
      name: string;
    };
  }) {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging
    } = useSortable({
      id: item.id
    });
    const style: React.CSSProperties = {
      transform: CSS.Translate.toString(transform),
      transition,
      opacity: isDragging ? 0.9 : 1
    };
    return <div ref={setNodeRef} style={style} className="grid grid-cols-1 md:grid-cols-12 items-center gap-2 md:gap-4 px-3 py-3">
        <div className="md:col-span-6 flex items-center gap-2">
          <button className="cursor-grab active:cursor-grabbing p-1 rounded-sm text-muted-foreground" aria-label="Arrastar para reordenar" {...attributes} {...listeners}>
            <GripVertical className="h-4 w-4 opacity-70" />
          </button>
          <span className="truncate font-medium">{item.name}</span>
        </div>
        <div className="md:col-span-6 flex items-center justify-end gap-2">
          <Button size="sm" variant="outline" onClick={() => {
            if (!checkPermission('edit')) return;
            setEditing(item.id);
            setEditName(item.name);
          }}>
            <Edit className="h-4 w-4" />
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" variant="destructive">
                <Trash2 className="h-4 w-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Excluir forma de pagamento</AlertDialogTitle>
                <AlertDialogDescription>
                  Tem certeza que deseja excluir "{item.name}"? Esta ação não pode ser desfeita.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={() => {
                  if (!checkPermission('delete')) return;
                  removePaymentMethod(item.id);
                  toast({
                    title: "Forma de pagamento excluída",
                    description: `"${item.name}" foi removido.`,
                    duration: 500
                  });
                }}>
                  Confirmar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>;
  }
  return <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Formas de Pagamento</span>
          <span className="text-sm font-normal text-muted-foreground">
            {paymentMethods.length} {paymentMethods.length === 1 ? 'forma' : 'formas'}
          </span>
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <ImportCadastroButton type="paymentMethods" label="Importar Formas de Pagamento" />
          <div className="ml-auto">
            <Button size="sm" onClick={() => { if (!checkPermission('create')) return; setCreating(true); }}>
              <Plus className="h-4 w-4 md:mr-1" />
              <span className="hidden md:inline">Adicionar</span>
            </Button>
          </div>
        </div>

        <div className="border rounded-md">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={orderedPaymentMethods.map(p => p.id)} strategy={verticalListSortingStrategy}>
              <div className="divide-y">
                {orderedPaymentMethods.map(p => <PaymentMethodRow key={p.id} item={p} />)}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      </CardContent>

      {/* Modal de edição */}
      <Dialog open={!!editing} onOpenChange={open => {
      if (!open) setEditing(null);
    }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Forma de Pagamento</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="edit-payment-name">Nome</Label>
              <Input id="edit-payment-name" value={editName} onChange={e => setEditName(e.target.value)} className="h-10" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancelar</Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button>Salvar</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Confirmar alterações</AlertDialogTitle>
                  <AlertDialogDescription>Deseja salvar as alterações desta forma de pagamento?</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Voltar</AlertDialogCancel>
                  <AlertDialogAction onClick={() => {
                  if (!editing) return;
                  
                  // Validação de duplicação (exclui o próprio item)
                  const normalizedEditName = (editName || "").trim().toLowerCase();
                  const isDuplicate = paymentMethods.some(
                    pm => 
                      pm.id !== editing && 
                      pm.name.toLowerCase() === normalizedEditName
                  );
                  
                  if (isDuplicate) {
                    toast({
                      title: "Nome duplicado",
                      description: `Já existe uma forma de pagamento com o nome "${editName?.trim()}".`,
                      variant: "destructive",
                      duration: 3000
                    });
                    return;
                  }
                  
                  updatePaymentMethod(editing, editName || paymentMethods.find(p => p.id === editing)?.name || "");
                  toast({
                    title: "Forma de pagamento atualizada",
                    duration: 500
                  });
                  setEditing(null);
                }}>Confirmar</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de nova forma de pagamento */}
      <Dialog open={creating} onOpenChange={open => setCreating(open)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nova Forma de Pagamento</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="new-payment-name">Nome</Label>
              <Input id="new-payment-name" value={newName} onChange={e => setNewName(e.target.value)} className="h-10" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreating(false)}>Cancelar</Button>
            <Button onClick={() => {
            if (!newName.trim()) return;
            
            // Validação de duplicação
            const normalizedName = newName.trim().toLowerCase();
            const isDuplicate = paymentMethods.some(
              pm => pm.name.toLowerCase() === normalizedName
            );
            
            if (isDuplicate) {
              toast({
                title: "Nome duplicado",
                description: `Já existe uma forma de pagamento com o nome "${newName.trim()}".`,
                variant: "destructive",
                duration: 3000
              });
              return;
            }
            
            addPaymentMethod(newName.trim());
            setNewName("");
            setCreating(false);
          }}>Adicionar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alert de permissão */}
      <PermissionAlert 
        open={showAlert} 
        onOpenChange={setShowAlert} 
        action={alertAction} 
      />
    </Card>;
};

// Supplier Manager Expanded
export const SupplierManagerExpanded: React.FC = () => {
  const {
    suppliers,
    addSupplier,
    updateSupplier,
    removeSupplier
  } = useFinance();
  const { checkPermission, canView, showAlert, setShowAlert, alertAction, loading } = usePermissionCheck('suppliers');
  const [creating, setCreating] = React.useState(false);
  const [editing, setEditing] = React.useState<string | null>(null);
  const [newName, setNewName] = React.useState("");
  const [editName, setEditName] = React.useState("");
  const [newCnpj, setNewCnpj] = React.useState("");
  const [newAnexo, setNewAnexo] = React.useState("");
  const [editCnpj, setEditCnpj] = React.useState("");
  const [editAnexo, setEditAnexo] = React.useState("");

  // DnD functionality (order persisted locally only)
  const sensors = useSensors(useSensor(PointerSensor));
  const [order, setOrder] = React.useState<string[]>(() => {
    const saved = localStorage.getItem('supplierOrder');
    return saved ? JSON.parse(saved) : [];
  });
  React.useEffect(() => {
    localStorage.setItem('supplierOrder', JSON.stringify(order));
  }, [order]);
  React.useEffect(() => {
    const ids = suppliers.map(s => s.id);
    setOrder(prev => {
      const filtered = prev.filter(id => ids.includes(id));
      const missing = ids.filter(id => !prev.includes(id));
      return [...filtered, ...missing];
    });
  }, [suppliers]);
  const orderedSuppliers = React.useMemo(() => {
    const map = new Map(suppliers.map(s => [s.id, s]));
    const ordered = order.map(id => map.get(id)).filter(Boolean) as typeof suppliers;
    const remaining = suppliers.filter(s => !order.includes(s.id));
    return [...ordered, ...remaining];
  }, [suppliers, order]);
  const handleDragEnd = (event: DragEndEvent) => {
    const {
      active,
      over
    } = event;
    if (!over || active.id === over.id) return;
    const current = orderedSuppliers.map(s => s.id);
    const from = current.indexOf(String(active.id));
    const to = current.indexOf(String(over.id));
    if (from !== -1 && to !== -1) {
      setOrder(arrayMove(current, from, to));
    }
  };
  const handleAddSupplier = async () => {
    if (!newName.trim()) return;
    
    // Validação de duplicação
    const normalizedName = newName.trim().toLowerCase();
    const isDuplicate = suppliers.some(
      s => s.name.toLowerCase() === normalizedName
    );
    
    if (isDuplicate) {
      toast({
        title: "Nome duplicado",
        description: `Já existe um fornecedor com o nome "${newName.trim()}".`,
        variant: "destructive",
        duration: 3000
      });
      return;
    }
    
    await addSupplier(newName.trim(), { cnpj: newCnpj.trim() || undefined, anexo: newAnexo.trim() || undefined });
    setNewName("");
    setNewCnpj("");
    setNewAnexo("");
    setCreating(false);
    toast({
      title: "Fornecedor criado",
      description: `"${newName.trim()}" foi adicionado.`,
      duration: 1000
    });
  };
  const handleUpdateSupplier = async () => {
    if (!editing || !editName.trim()) return;
    
    // Validação de duplicação (exclui o próprio item)
    const normalizedEditName = editName.trim().toLowerCase();
    const isDuplicate = suppliers.some(
      s => 
        s.id !== editing && 
        s.name.toLowerCase() === normalizedEditName
    );
    
    if (isDuplicate) {
      toast({
        title: "Nome duplicado",
        description: `Já existe um fornecedor com o nome "${editName.trim()}".`,
        variant: "destructive",
        duration: 3000
      });
      return;
    }
    
    await updateSupplier(editing, editName.trim(), { cnpj: editCnpj.trim() || undefined, anexo: editAnexo.trim() || undefined });
    setEditing(null);
    toast({
      title: "Fornecedor atualizado",
      duration: 500
    });
  };
  const handleRemoveSupplier = async (id: string) => {
    if (!checkPermission('delete')) return;
    const supplier = suppliers.find(s => s.id === id);
    await removeSupplier(id);
    toast({
      title: "Fornecedor excluído",
      description: `"${supplier?.name}" foi removido.`,
      duration: 500
    });
  };

  // Check view permission
  if (!loading && !canView()) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Fornecedores</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
            <p>Você não tem permissão para visualizar este módulo.</p>
            <p className="text-sm mt-2">Solicite ao proprietário da organização que altere suas permissões.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  function SupplierRow({
    item
  }: {
    item: {
      id: string;
      name: string;
    };
  }) {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging
    } = useSortable({
      id: item.id
    });
    const style: React.CSSProperties = {
      transform: CSS.Translate.toString(transform),
      transition,
      opacity: isDragging ? 0.9 : 1
    };
    return <div ref={setNodeRef} style={style} className="grid grid-cols-1 md:grid-cols-12 items-center gap-2 md:gap-4 px-3 py-3">
        <div className="md:col-span-8 flex items-center gap-2">
          <button className="cursor-grab active:cursor-grabbing p-1 rounded-sm text-muted-foreground" aria-label="Arrastar para reordenar" {...attributes} {...listeners}>
            <GripVertical className="h-4 w-4 opacity-70" />
          </button>
          <span className="truncate font-medium">{item.name}</span>
        </div>
        <div className="md:col-span-4 flex items-center justify-end gap-2">
          <Button size="sm" variant="outline" onClick={() => {
            if (!checkPermission('edit')) return;
            setEditing(item.id);
            setEditName(item.name);
            setEditCnpj((item as any).cnpj || "");
            setEditAnexo((item as any).anexo || "");
          }}>
            <Edit className="h-4 w-4" />
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" variant="destructive"><Trash2 className="h-4 w-4" /></Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Excluir fornecedor</AlertDialogTitle>
                <AlertDialogDescription>Tem certeza que deseja excluir "{item.name}"? Esta ação não pode ser desfeita.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={() => handleRemoveSupplier(item.id)}>Confirmar</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>;
  }
  return <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Fornecedores</span>
          <span className="text-sm font-normal text-muted-foreground">{suppliers.length} {suppliers.length === 1 ? 'fornecedor' : 'fornecedores'}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <ImportCadastroButton type="suppliers" label="Importar Fornecedores" />
          <div className="ml-auto">
            <Button size="sm" onClick={() => { if (!checkPermission('create')) return; setCreating(true); }}>
              <Plus className="h-4 w-4 md:mr-1" />
              <span className="hidden md:inline">Adicionar</span>
            </Button>
          </div>
        </div>

        <div className="border rounded-md">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={orderedSuppliers.map(s => s.id)} strategy={verticalListSortingStrategy}>
              <div className="divide-y">
                {orderedSuppliers.map(supplier => <SupplierRow key={supplier.id} item={supplier} />)}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      </CardContent>

      {/* Modal de edição */}
      <Dialog open={!!editing} onOpenChange={open => {
      if (!open) setEditing(null);
    }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Fornecedor</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="edit-supplier-name" required>Nome</Label>
              <Input id="edit-supplier-name" value={editName} onChange={e => setEditName(e.target.value)} className="h-10" required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-supplier-cnpj">CNPJ</Label>
              <Input id="edit-supplier-cnpj" value={editCnpj} onChange={e => setEditCnpj(e.target.value)} className="h-10" placeholder="Opcional" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-supplier-anexo">Observações Gerais</Label>
              <Input id="edit-supplier-anexo" value={editAnexo} onChange={e => setEditAnexo(e.target.value)} className="h-10" placeholder="Opcional" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button onClick={handleUpdateSupplier}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de novo fornecedor */}
      <Dialog open={creating} onOpenChange={open => setCreating(open)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo Fornecedor</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="new-supplier-name" required>Nome</Label>
              <Input id="new-supplier-name" value={newName} onChange={e => setNewName(e.target.value)} className="h-10" required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-supplier-cnpj">CNPJ</Label>
              <Input id="new-supplier-cnpj" value={newCnpj} onChange={e => setNewCnpj(e.target.value)} className="h-10" placeholder="Opcional" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-supplier-anexo">Observações Gerais</Label>
              <Input id="new-supplier-anexo" value={newAnexo} onChange={e => setNewAnexo(e.target.value)} className="h-10" placeholder="Opcional" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreating(false)}>Cancelar</Button>
            <Button onClick={handleAddSupplier}>Adicionar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alert de permissão */}
      <PermissionAlert 
        open={showAlert} 
        onOpenChange={setShowAlert} 
        action={alertAction} 
      />
    </Card>;
};


